import i18next from 'i18next'
import _ from 'lodash'
import { ReactText } from 'react'
import { ReportTableRow, VariableType } from '../../../../components/Table/types'
import { Column } from '../../../../redux/context/periodGroups/hooks'
import { ReportDataType } from '../../../../redux/context/reports/types'
import { formatValueByType } from '../../../../utils/helpers'
import { GraphType } from './chart/ChartTypeSelect'
import { CustomReportCategory } from '../../../../redux/context/customReports/typesCategory'
import { CustomReportChartSection } from '../../../../redux/context/customReports/typesSection'
import {
  CustomReportVariable,
  FormulaVariable,
  KeyFigureVariable,
  SourceVariable
} from '../../../../redux/context/customReports/typesVariable'
import { CustomReport, CustomReportGroup } from '../../../../redux/context/customReports/types'
import { CustomReportStructure } from '../../../../features/customReport/types/CustomReportStructure'
import { getTitleByLanguage } from './customReportTable/utils'

export type FormatterType = Highcharts.TooltipFormatterContextObject & {
  point?: {
    negative?: boolean
    percent?: boolean
  }
}

export function formatter(this: FormatterType) {
  const percent = this.percentage?.toFixed(1)
  const y = formatValueByType((this.point.negative && this.y ? -this.y : this.y) as ReactText, VariableType.absolute, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1
  })
  return `${this.key}:<b>${percent}%</b>${
    !this.point.percent ? `<br>${i18next.t('global:currency-symbol')}: <b>${y}</b>` : ''
  }`
}

export const getCustomChartPlotOptions = (
  type: GraphType,
  section: CustomReportChartSection
): [GraphType, Highcharts.Options['plotOptions']] => {
  switch (type) {
    case GraphType.line:
      return [
        GraphType.line,
        {
          series: {
            animation: false
          },
          line: {
            stacking: section?.style?.stacking,
            dataLabels: {
              enabled: section.style?.showDataLabels === 'true',
              rotation: 270,
              x: 0,
              y: -10
            }
          }
        }
      ]
    case GraphType.spline:
      return [
        GraphType.spline,
        {
          series: {
            animation: false
          },
          spline: {
            stacking: section?.style?.stacking,
            dataLabels: {
              enabled: section.style?.showDataLabels === 'true',
              rotation: 270,
              x: 0,
              y: -10
            }
          }
        }
      ]
    case GraphType.areaspline:
      return [
        GraphType.column,
        {
          series: {
            animation: false
          },
          areaspline: {
            stacking: section?.style?.stacking,
            dataLabels: {
              enabled: section?.style?.showDataLabels === 'true',
              rotation: 270,
              x: 0,
              y: -10
            }
          }
        }
      ]

    case GraphType.bar:
      return [
        GraphType.bar,
        {
          series: {
            animation: false,
            dataSorting: {
              enabled: true,
              sortKey: 'y'
            }
          },
          bar: {
            grouping: true,
            stacking: section?.style?.stacking,
            dataLabels: {
              enabled: section.style?.showDataLabels === 'true',
              rotation: 0,
              x: 10,
              y: 0
            }
          }
        }
      ]
    case GraphType.column:
    default:
      return [
        GraphType.column,
        {
          series: {
            animation: false
          },
          column: {
            grouping: true,
            stacking: section?.style?.stacking,
            dataLabels: {
              enabled: section.style?.showDataLabels === 'true',
              rotation: 270,
              x: 0,
              y: -10
            }
          }
        }
      ]
  }
}

export const isLeaf = (category: CustomReportCategory): boolean => {
  if (category.type === 'function') return true
  return !category.children || category.children.length < 1
}

export const getLeafNodes = (nodes: ReportTableRow[] | CustomReportCategory[], result: Column[] = []) => {
  for (let i = 0, { length } = nodes; i < length; i += 1) {
    if (!nodes[i].children || nodes[i].children.length === 0) {
      result.push(nodes[i] as unknown as Column)
    } else {
      // eslint-disable-next-line no-param-reassign
      result = getLeafNodes(nodes[i].children, result)
    }
  }
  return result.slice(0) as Column[]
}

export const getTitle = (reportTableRow: ReportTableRow | CustomReportVariable) => {
  if (reportTableRow.params?.dimension) {
    return `${reportTableRow.name} - ${reportTableRow.params.dimension.name}`
  }
  return reportTableRow.name
}

export const getColumn = (category: CustomReportCategory): any => {
  const getChildren = (c: CustomReportCategory) => {
    switch (c.type) {
      case 'function':
        return undefined
      default:
        return c.children && c.children.map(getColumn)
    }
  }

  return {
    ...category,
    dataIndex: `${category.dataType || ReportDataType.actuals}-${category.id}`,
    key: category.id,
    children: getChildren(category)
  }
}

export const getCategoryVariableType = (category?: CustomReportCategory | null): VariableType | undefined => {
  if (
    (category?.type === 'function' &&
      (category?.value === 'growth' || category?.value === 'commonSize' || category?.value === 'percentage')) ||
    category?.variableType === VariableType.percentage
  ) {
    return VariableType.percentage
  }
  return undefined
}

const variableData = (variable: CustomReportVariable) => {
  const formulaId = (variable as FormulaVariable).formulaId || null // Type assertion here ensures safe access.
  const keyFigureId = (variable as KeyFigureVariable).keyFigureId || null // Type assertion here ensures safe access.
  const source = (variable as SourceVariable).source || null // Type assertion here ensures safe access.

  const { params, params: { dimension } = {} } = variable

  return {
    params,
    keyFigureId,
    formulaId,
    source,
    dimensionId: dimension?.dimensionId
  }
}

export const variablesAreEqual = (oldVariable: CustomReportVariable, newVariable: CustomReportVariable) => {
  return _.isEqual(variableData(newVariable), variableData(oldVariable))
}

export const getMaxSeriesValue = (chartSeries: Highcharts.Options[]) => {
  const seriesData = []
  for (const serie of chartSeries) {
    seriesData.push(...(serie.data as number[]))
  }
  return Math.abs(Math.max(...seriesData) * 1.3)
}

export const getVariable = (variable: CustomReportVariable, order: number) => {
  const formulaId = (variable as FormulaVariable).formulaId || null // Type assertion here ensures safe access.
  const keyFigureId = (variable as KeyFigureVariable).keyFigureId || null // Type assertion here ensures safe access.
  const source = (variable as SourceVariable).source || null // Type assertion here ensures safe access.

  const { rowType, style, name, type, params, id, nameFi, nameEn, nameSv } = variable
  const [dataType, budgetingScenarioId] = params?.dataType || []

  const paramsNew = {
    ...params,
    dataType,
    dimension: params?.dimension?.dimensionId ? params.dimension : undefined,
    budgetingScenario: budgetingScenarioId ? { id: budgetingScenarioId } : undefined
  }

  return {
    id,
    name,
    rowType,
    type,
    formulaId,
    keyFigureId,
    source,
    order,
    style,
    nameFi,
    nameEn,
    nameSv,
    params: paramsNew
  }
}

export const getCustomReportTree = (customReportList: (CustomReport | CustomReportGroup)[]) => {
  const traverse: any = (customReport: CustomReport) => {
    if (customReportList) {
      const children = customReportList
        .sort((a, b) => (a.order > b.order ? 1 : -1))
        .filter(d => d.groupId === customReport.id)
      return {
        ...customReport,
        title: getTitleByLanguage(customReport),
        children: children.length > 0 ? children.map(child => traverse(child)) : undefined
      }
    }
    return []
  }
  return (
    customReportList
      ?.filter(d => !d.groupId || !customReportList.find(i => i.id === d.groupId))
      .sort((a, b) => (a.order > b.order ? 1 : -1))
      .map(root => traverse(root)) || []
  )
}

export const firstReport = (data?: CustomReportStructure[]): CustomReport | undefined => {
  if (!data) return undefined
  let result
  function iter(a: CustomReportStructure) {
    if (!a?.group) {
      result = a
      return true
    }
    return Array.isArray(a.children) && a.children.some(iter)
  }
  data.some(iter)
  return result
}
